import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { activityApi } from "apis";
import {
  MetricValuesPaginationRequestDto,
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "libs/cellera-sdk";
import { isNil } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

import LayoutWrapper from "../LayoutWrapper";
import { MetricCard, SectionWrapper } from "./index";

interface DailyWellBeingScoreProps {
  patientId: string;
}

export const DailyWellBeingScore: React.FC<DailyWellBeingScoreProps> = ({
  patientId,
}: DailyWellBeingScoreProps) => {
  const { colors } = useCelleraTheme();
  const [lastMonthChecked, setLastMonthChecked] = useState<string>(
    new Date().getMonth().toString()
  );
  const [lastYearChecked, setLastYearChecked] = useState<string>(
    new Date().getFullYear().toString()
  );
  const [lastValue, setLastValue] = useState<{
    score: string | null;
    lastChecked: string | null;
  }>({ score: null, lastChecked: null });
  const { data: wellBeingScoreData, isLoading } = useQuery({
    queryKey: ["well-being-score", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      const request: MetricValuesPaginationRequestDto = {
        metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.WellBeing,
        page: 1,
        pageSize: 1,
        orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
      };

      return activityApi.getMetricValuesPaginatedByPatientId({
        patientId,
        metricValuesPaginationRequestDto: request,
      });
    },
    enabled: !!patientId,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const latestRecord = wellBeingScoreData?.data?.[0];
    if (!isNil(latestRecord)) {
      setLastValue({
        score: latestRecord.valueInt?.toString() || "No Data",
        lastChecked: latestRecord?.startDate
          ? new Date(latestRecord?.startDate).toLocaleString()
          : "Never",
      });
      if (!isNil(latestRecord?.startDate)) {
        setLastMonthChecked(moment(latestRecord?.startDate).month().toString());
        setLastYearChecked(moment(latestRecord?.startDate).year().toString());
      }
    }
  }, [wellBeingScoreData]);

  return (
    <SectionWrapper
      title="Daily Well Being Score"
      spacing="normal"
      sx={{ pb: 2, pt: 2 }}
    >
      <LayoutWrapper itemsPerRow={1}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: colors.primary.main }} />
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Loading daily well being score data...
            </Typography>
          </Box>
        ) : (
          <MetricCard
            key={MetricValuesPaginationRequestDtoMetricCodeEnum.WellBeing}
            metric={{
              id: MetricValuesPaginationRequestDtoMetricCodeEnum.WellBeing,
              name: "Well Being Score",
              hasData: !isNil(lastValue?.score),
              value: lastValue?.score || "No Data",
              fallbackInfo: {
                message: "No recent data available",
                suggestion: "Consider manual measurement",
                lastRecorded: "Not available",
              },
              lastChecked: lastValue?.lastChecked || "Never",
            }}
            onNext={() =>
              navigate(
                `/profile/daily-well-being-score/${patientId}/year/${lastYearChecked}/month/${lastMonthChecked}`
              )
            }
          />
        )}
      </LayoutWrapper>
    </SectionWrapper>
  );
};
